import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

function Login({ onLogin }) {
  const { t } = useLanguage();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({ email: '', username: '', password: '' });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const endpoint = isRegister ? '/auth/register' : '/auth/login';
    try {
      const payload = isRegister
        ? formData
        : { identifier: formData.username, password: formData.password };
      const { data } = await api.post(endpoint, payload);
      if (isRegister) {
        setIsRegister(false);
        setError('Account created! Please log in.');
      } else {
        onLogin(data);
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      const message = data?.error || data?.message || err.message;

      console.error('Authentication Error:', { status, data, message });

      if (status === 401) {
        setError('Invalid credentials. Please verify your identity identifier and access key.');
      } else if (status === 404) {
        setError('Authentication gateway not found (404). Check API proxy configuration.');
      } else if (status === 500) {
        const detailMessage = data?.details ? ` - ${data.details}` : '';
        const errorCode = data?.code ? ` [Code: ${data.code}]` : '';
        setError(`Core system error (500)${detailMessage}${errorCode}. The backend might be experiencing technical difficulties.`);
      } else {
        setError(`Authentication Failed: ${message} (Status: ${status || 'Unknown'})`);
      }
    }
  };

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      background: 'var(--bg-apex)',
      backgroundImage: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.05), transparent 70%)'
    }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '440px', padding: '3rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{
            background: 'var(--accent-primary)',
            width: '80px', height: '80px', borderRadius: '20px', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', marginBottom: '2rem',
            boxShadow: 'var(--glow-indigo)'
          }}>
            <span style={{ fontWeight: 900, color: 'var(--btn-text)', fontSize: '2.5rem' }}>A</span>
          </div>
          <h2 style={{ fontSize: '2.5rem', color: 'var(--text-heading)', marginBottom: '0.75rem', letterSpacing: '-0.03em' }}>
            {isRegister ? t('access_initialization') : t('neo_access')}
          </h2>
          <p style={{ color: 'var(--text-body)', fontSize: '1rem', fontWeight: 500 }}>
            {isRegister ? t('register_subtitle') : t('login_subtitle_auth')}
          </p>
        </div>

        {error && (
          <div style={{
            background: 'rgba(244, 114, 182, 0.1)', border: '1px solid rgba(244, 114, 182, 0.2)',
            color: 'var(--accent-magenta)', padding: '1rem', borderRadius: '12px',
            marginBottom: '1.5rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 600
          }}>
            {typeof error === 'object' ? (error.message || JSON.stringify(error)) : error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {isRegister && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem' }}>{t('email_address')}</label>
              <input
                className="cyber-input"
                type="text"
                placeholder="identity@ap-group.org"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem' }}>{t('identity_identifier')}</label>
            <input
              className="cyber-input"
              type="text"
              placeholder={isRegister ? "Select unique alias" : t('identity_identifier')}
              required
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem' }}>{t('access_key')}</label>
            <input
              className="cyber-input"
              type="password"
              placeholder="••••••••"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
          </div>

          <button type="submit" className="btn-cyber" style={{ marginTop: '1rem', padding: '1.25rem' }}>
            {isRegister ? t('authorize_account') : t('initialize_session')}
          </button>
        </form>

        {/* Forgot Password link — only show on login mode */}
        {!isRegister && (
          <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
            <Link
              to="/forgot-password"
              style={{ color: 'var(--text-muted)', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}
            >
              {t('forgot_password')}
            </Link>
          </div>
        )}

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 500 }}>
            {isRegister ? t('already_network') : t('ap_member')}
            <button
              onClick={() => setIsRegister(!isRegister)}
              style={{ background: 'none', color: 'var(--accent-primary)', fontWeight: 700, marginLeft: '0.5rem', outline: 'none', border: 'none', cursor: 'pointer' }}
            >
              {isRegister ? t('sign_in') : t('register_link')}
            </button>
          </p>
          {!isRegister && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 500, marginTop: '0.75rem' }}>
              {t('visiting_us')}
              <Link
                to="/register-guest"
                style={{ color: 'var(--accent-cyan)', fontWeight: 700, marginLeft: '0.5rem', textDecoration: 'none' }}
              >
                {t('guest_access')}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;

